/// <reference types="w3c-web-serial" />
import { ESPLoader, type FlashSizeValues, type IEspLoaderTerminal, type Transport } from "esptool-js";
import type { FlashPlan } from "idfweb-build";
import { chipLabel, chipNameOfTarget, targetNameOfChip, type ChipIdentity } from "idfweb-chip";
import { closeQuietly, detectChip, hardReset, SlipTransport } from "idfweb-chip/loader";
import type { Reporter, Task } from "idfweb-tasks";

import type { Board, BoardEvents, ConnectContext, Connector, Disk, RememberedBoard } from "./board.ts";
import { readFlash } from "./flash-read.ts";
import { awaitPortReturn, type PortReturn } from "./port-return.ts";

/** What ESP-IDF's console runs at, and what the ROM loader is spoken to at. */
const MONITOR_BAUD = 115200;

/** What esptool raises the rate to once its stub is running. */
const FLASH_BAUD = 460800;

/** Web Serial API reference; available in Chromium-based browsers. */
const SERIAL: Serial | undefined =
    typeof navigator !== "undefined" && "serial" in navigator ? navigator.serial : undefined;

/** How long a reconnect gives another tab to let go of the port before it tries again. */
const RELEASE_WAIT_MS = 1000;

/** Regular expression matching block progress lines from esptool-js. */
const PER_BLOCK = /^Writing at 0x[0-9a-f]+\.\.\. \(\d+%\)$/;

/** The chip's flash, the one disk a serial board offers. */
const FLASH_DISK = "flash";
const FLASH_SECTOR = 0x1000;
/** The Web Serial buffer while the loader has the port: room for a flash read the stub sends ahead. */
const LOADER_BUFFER = 0x100000;

/** Known USB-to-UART vendor identifiers, for a port whose chip is not known. */
const USB_VENDORS = new Map<number, string>([
    [0x303a, "Espressif"],
    [0x10c4, "Silicon Labs"],
    [0x1a86, "WCH"],
    [0x0403, "FTDI"],
]);

/** A board on a serial port the person picks, flashed with esptool-js. */
export const serialConnector: Connector = {
    id: "serial",
    label: "Board (WebSerial)",
    unavailable: SERIAL === undefined ? "Web Serial is only available in Chrome and Edge." : undefined,
    connect: connectSerial,
    reconnect: reconnectSerial,
};

async function connectSerial(context: ConnectContext): Promise<Board | undefined> {
    const serial = SERIAL;
    if (serial === undefined) return undefined;
    let port: SerialPort;
    try {
        port = await serial.requestPort();
    } catch (e) {
        // Port selection cancelled by user.
        if (e instanceof Error && (e.name === "NotFoundError" || e.name === "AbortError")) return undefined;
        throw e;
    }
    return connectPort(port, serialReturns(serial), remember(port.getInfo()), context);
}

/**
 * Web Serial gives a port's USB ids and no serial number, so the board is
 * found again only when one granted port has its ids.
 */
async function reconnectSerial(remembered: RememberedBoard, context: ConnectContext): Promise<Board | undefined> {
    const serial = SERIAL;
    if (serial === undefined) return undefined;
    const matching = (await serial.getPorts()).filter((port) => {
        const info = port.getInfo();
        return info.usbVendorId === remembered.usbVendorId && info.usbProductId === remembered.usbProductId;
    });
    if (matching.length !== 1) return undefined;
    await awaitRelease(matching[0]!, remembered);
    return connectPort(matching[0]!, serialReturns(serial), remembered, context);
}

/**
 * Open and close `port` to see that no other tab holds it. The tab this one
 * took the project from closes its port once it learns of that, so an open
 * that fails is tried once more a little later. The WebUSB connector shares this.
 */
export async function awaitRelease(port: SerialPort, remembered: RememberedBoard): Promise<void> {
    const tryOpen = async (): Promise<void> => {
        try {
            await port.open({ baudRate: MONITOR_BAUD });
        } finally {
            await port.close().catch(() => {});
        }
    };
    try {
        await tryOpen();
    } catch {
        await new Promise((resolve) => setTimeout(resolve, RELEASE_WAIT_MS));
        try {
            await tryOpen();
        } catch (e) {
            const name = typeof remembered.chip === "string" ? remembered.chip : describePort(port.getInfo());
            throw new Error(`${name} does not open; another tab may have it: ${message(e)}`);
        }
    }
}

function remember(info: SerialPortInfo): RememberedBoard {
    return { connector: "serial", usbVendorId: info.usbVendorId, usbProductId: info.usbProductId };
}

function serialReturns(serial: Serial): PortReturns {
    return { await: (lost, found) => awaitPortReturn(serial, lost.getInfo(), found) };
}

/** How a connector finds its board's port again after the board left USB. */
export interface PortReturns {
    await(lost: SerialPort, found: (port: SerialPort) => void): PortReturn;
}

/**
 * Ask the board on `port` which chip it is, then open it for the monitor and
 * reset it into its application. The chip names the board, and is kept with
 * `remembered` for the messages of a reconnect. The WebUSB connector shares this.
 */
export async function connectPort(
    port: SerialPort,
    returns: PortReturns,
    remembered: RememberedBoard,
    context: ConnectContext,
): Promise<Board | undefined> {
    const transport = new SlipTransport(port, false);
    const chip = await detect(transport, context.reporter);
    if (chip !== undefined && !(await context.chipFound(chip))) return undefined;

    const kept = chip === undefined ? remembered : { ...remembered, chip: chipLabel(chip) };
    const board = new SerialBoard(chip, returns, kept, context.events, context.reporter);
    await board.open(port, transport);
    context.reporter.message("info", `Connected to ${board.label} at ${MONITOR_BAUD} baud.`);
    // Reset chip to boot application after ROM detection. A board that drops
    // off USB here is waited for, like after any reset, so it stays connected.
    try {
        await hardReset(transport);
    } catch (e) {
        context.reporter.message("error", `Could not reset the board: ${message(e)}`);
    }
    return board;
}

/** The chip on the port, from its ROM loader; undefined when it does not answer. */
async function detect(transport: Transport, reporter: Reporter): Promise<ChipIdentity | undefined> {
    const task = reporter.task("Asking the board which chip it is");
    try {
        const chip = await detectChip(
            transport,
            loaderTerminal((line) => task.progress(0, { detail: line })),
        );
        task.done();
        return chip;
    } catch (e) {
        task.failed(`Could not detect the chip: ${message(e)}`);
        return undefined;
    }
}

/**
 * The open port and the monitor's read loop over it. A USB board that
 * disconnects, as one with a USB-Serial/JTAG does when it resets, is waited
 * for and reopened; meanwhile the board is not ready.
 */
class SerialBoard implements Board {
    readonly remembered: RememberedBoard;
    /** Undefined when the ROM loader did not answer; the port's USB ids name the board then. */
    readonly #chip: ChipIdentity | undefined;
    readonly #returns: PortReturns;
    readonly #events: BoardEvents;
    readonly #reporter: Reporter;
    #port: SerialPort | undefined;
    #transport: Transport | undefined;
    /** The monitor's read loop, and the reader a pause cancels. */
    #monitor: Promise<void> | undefined;
    #reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    #monitoring = false;
    /** Reconnection watcher for a disconnected board. */
    #waiting: { info: SerialPortInfo; watch: PortReturn } | undefined;
    #closed = false;
    /** The flash's size in bytes, which the stub reports at the first read. */
    #flashSize: number | undefined;
    /** Operations on the port, one at a time. */
    #queue: Promise<unknown> = Promise.resolve();

    readonly #onLost = (): void => this.#lost();

    constructor(
        chip: ChipIdentity | undefined,
        returns: PortReturns,
        remembered: RememberedBoard,
        events: BoardEvents,
        reporter: Reporter,
    ) {
        this.#chip = chip;
        this.#returns = returns;
        this.remembered = remembered;
        this.#events = events;
        this.#reporter = reporter;
    }

    get label(): string {
        const info = this.#port?.getInfo() ?? this.#waiting?.info;
        if (info === undefined) return "no device";
        const name = this.#chip === undefined ? describePort(info) : chipLabel(this.#chip);
        return this.#port === undefined ? `waiting for ${name}` : name;
    }

    get detail(): string | undefined {
        const chip = this.#chip;
        const info = this.#port?.getInfo() ?? this.#waiting?.info;
        if (chip === undefined || info === undefined) return undefined;
        const parts = [chip.description, chip.mac === undefined ? undefined : `MAC ${chip.mac}`];
        return [...parts.filter((part) => part !== undefined), `on ${describePort(info)}`].join(", ");
    }

    get ready(): boolean {
        return this.#port !== undefined;
    }

    async open(port: SerialPort, transport: Transport): Promise<void> {
        await openMonitor(transport);
        this.#port = port;
        this.#transport = transport;
        port.addEventListener("disconnect", this.#onLost);
        this.#startMonitor(port);
    }

    flash(plan: FlashPlan, task: Task): Promise<void> {
        return this.#withLoader((transport) => writeImages(transport, plan, task));
    }

    disks(): Disk[] {
        return [
            { id: FLASH_DISK, kind: "internal-nor", label: "Flash", size: this.#flashSize, eraseSize: FLASH_SECTOR },
        ];
    }

    async readDisk(disk: string, offset: number, length: number, task: Task): Promise<Uint8Array> {
        this.#checkRange(disk, offset, length);
        return await this.#withLoader(async (transport) => {
            const loader = await this.#stub(transport, task);
            this.#checkRange(disk, offset, length);
            task.progress(0, { total: length, detail: `reading ${length} bytes at 0x${offset.toString(16)}` });
            return await readFlash(loader, offset, length, (read) => task.progress(read));
        });
    }

    async writeDisk(disk: string, offset: number, data: Uint8Array, task: Task): Promise<void> {
        this.#checkRange(disk, offset, data.length);
        if (offset % FLASH_SECTOR !== 0 || data.length % FLASH_SECTOR !== 0) {
            throw new Error("A write to flash covers whole 4 KB sectors.");
        }
        return await this.#withLoader(async (transport) => {
            const loader = await this.#stub(transport, task);
            this.#checkRange(disk, offset, data.length);
            task.progress(0, {
                total: data.length,
                detail: `writing ${data.length} bytes at 0x${offset.toString(16)}`,
            });
            await loader.writeFlash({
                fileArray: [{ data, address: offset }],
                flashMode: "keep",
                flashFreq: "keep",
                flashSize: "keep",
                eraseAll: false,
                compress: true,
                reportProgress: (_index, sent, total) => task.progress(total > 0 ? (sent / total) * data.length : 0),
            });
        });
    }

    /** The loader with its stub running, which reads flash, and the flash's size from the stub. */
    async #stub(transport: Transport, task: Task): Promise<ESPLoader> {
        task.progress(0, { detail: "connecting to the ROM loader" });
        const loader = new ESPLoader({
            transport,
            baudrate: FLASH_BAUD,
            // The stub sends a read ahead of the acks; with Chrome's 255-byte default, a long read loses bytes.
            serialOptions: { bufferSize: LOADER_BUFFER },
            terminal: loaderTerminal((line) => task.progress(0, { detail: line })),
        });
        await loader.main();
        const size = loader.flashSizeBytes((await loader.detectFlashSize()) as FlashSizeValues);
        if (size !== this.#flashSize) {
            this.#flashSize = size;
            this.#events.changed();
        }
        return loader;
    }

    #checkRange(disk: string, offset: number, length: number): void {
        if (disk !== FLASH_DISK) throw new Error(`The board has no disk "${disk}".`);
        const size = this.#flashSize;
        if (offset < 0 || length < 0 || (size !== undefined && offset + length > size)) {
            throw new Error(`0x${offset.toString(16)}+${length} is outside the flash.`);
        }
    }

    /**
     * Stop the monitor, run `what` with the port to itself, then reopen the
     * monitor and, when `what` succeeded, reset the board into its
     * application with the monitor reading its boot.
     */
    #withLoader<T>(what: (transport: Transport) => Promise<T>): Promise<T> {
        return this.#exclusive(async () => {
            const port = this.#port;
            const transport = this.#transport;
            if (port === undefined || transport === undefined) throw new Error("The board is not connected.");
            let done = false;
            try {
                await this.#stopMonitor();
                // esptool-js manages port connection directly during flashing.
                await closeQuietly(transport);
                try {
                    const result = await what(transport);
                    done = true;
                    return result;
                } finally {
                    // Close port after esptool operation finishes or throws.
                    await closeQuietly(transport);
                }
            } finally {
                await this.#resume(port, transport);
                if (done && this.#transport === transport) {
                    try {
                        await hardReset(transport);
                    } catch (e) {
                        this.#reporter.message("error", `Could not reset the board: ${message(e)}`);
                    }
                }
            }
        });
    }

    reset(): Promise<void> {
        return this.#exclusive(async () => {
            const transport = this.#transport;
            if (transport === undefined) throw new Error("The board is not connected.");
            await hardReset(transport);
        });
    }

    write(bytes: Uint8Array): Promise<void> {
        // Not through Transport.write, which frames the bytes for the ROM loader with SLIP.
        return this.#exclusive(async () => {
            const writer = this.#transport?.device.writable?.getWriter();
            if (writer === undefined) throw new Error("The serial port is not open for writing.");
            try {
                await writer.write(bytes);
            } finally {
                writer.releaseLock();
            }
        });
    }

    close(): Promise<void> {
        this.#closed = true;
        this.#stopWaiting();
        return this.#exclusive(async () => {
            const port = this.#port;
            const transport = this.#transport;
            this.#port = undefined;
            this.#transport = undefined;
            port?.removeEventListener("disconnect", this.#onLost);
            await this.#stopMonitor();
            if (transport !== undefined) await closeQuietly(transport);
        });
    }

    #exclusive<T>(what: () => Promise<T>): Promise<T> {
        const result = this.#queue.then(what, what);
        this.#queue = result.catch(() => {});
        return result;
    }

    // ---- the monitor -------------------------------------------------------

    #startMonitor(port: SerialPort): void {
        this.#monitoring = true;
        this.#monitor = this.#read(port).then(() => {
            // Disconnect if read loop terminates while monitoring is active.
            if (this.#monitoring) this.#lost();
        });
    }

    async #read(port: SerialPort): Promise<void> {
        const readable = port.readable;
        if (readable === null) return;
        const reader = readable.getReader();
        this.#reader = reader;
        try {
            for (;;) {
                const { value, done } = await reader.read();
                if (done) return;
                if (value !== undefined && value.length > 0) this.#events.data(value);
            }
        } catch (e) {
            if (this.#monitoring) this.#reporter.message("error", `Serial read failed: ${message(e)}`);
        } finally {
            reader.releaseLock();
            this.#reader = undefined;
        }
    }

    async #stopMonitor(): Promise<void> {
        this.#monitoring = false;
        try {
            // Cancel active reader to unblock read loop.
            await this.#reader?.cancel();
        } catch {
            // Ignore error if port is already closed.
        }
        const monitor = this.#monitor;
        this.#monitor = undefined;
        await monitor;
    }

    /** Reopens monitor connection following flash operation. */
    async #resume(port: SerialPort, transport: Transport): Promise<void> {
        if (this.#port !== port) return;
        try {
            await openMonitor(transport);
        } catch (e) {
            this.#reporter.message("error", `Could not reopen the port: ${message(e)}`);
            this.#lost();
            return;
        }
        this.#startMonitor(port);
    }

    #lost(): void {
        const port = this.#port;
        if (port === undefined) return;
        port.removeEventListener("disconnect", this.#onLost);
        this.#monitoring = false;
        this.#monitor = undefined;
        this.#port = undefined;
        this.#transport = undefined;
        const info = port.getInfo();
        if (info.usbVendorId === undefined) {
            this.#events.lost("The device disconnected.");
            return;
        }
        this.#reporter.message("warn", "The device disconnected.");
        // Watches for the USB device to return after a reset, and reopens it.
        const watch = this.#returns.await(port, (returned) => {
            this.#waiting = undefined;
            void this.#exclusive(() => this.#reopen(returned)).catch((e: unknown) =>
                this.#events.lost(`Could not reopen the port: ${message(e)}`),
            );
        });
        this.#waiting = { info, watch };
        this.#events.changed();
    }

    #stopWaiting(): void {
        this.#waiting?.watch.cancel();
        this.#waiting = undefined;
    }

    async #reopen(port: SerialPort): Promise<void> {
        if (this.#closed) return;
        await this.open(port, new SlipTransport(port, false));
        this.#reporter.message("info", `Reconnected to ${this.label}.`);
        this.#events.changed();
    }
}

async function writeImages(transport: Transport, plan: FlashPlan, task: Task): Promise<void> {
    // Compute total uncompressed bytes for task progress tracking.
    const total = plan.files.reduce((bytes, file) => bytes + file.data.length, 0);
    task.progress(0, { total, detail: "connecting to the ROM loader" });

    let position = 0;
    const loader = new ESPLoader({
        transport,
        // Flash stub runs at 460800 baud; initial ROM communication at 115200.
        baudrate: FLASH_BAUD,
        terminal: loaderTerminal((line) => task.progress(position, { detail: line })),
    });

    await loader.main();
    if (targetNameOfChip(loader.chip.CHIP_NAME) !== plan.chip) {
        throw new Error(
            `The board is ${loader.chip.CHIP_NAME} and the build is for ${chipNameOfTarget(plan.chip)}.` +
                " Nothing was written.",
        );
    }

    let current = -1;
    let written = 0;
    await loader.writeFlash({
        fileArray: plan.files.map((file) => ({ data: file.data, address: file.address })),
        flashMode: plan.flashMode,
        // The build wrote the frequency into the bootloader's header. esptool-js
        // rewrites it from a table without 48m, which leaves an ESP32-H2 unbootable.
        flashFreq: "keep",
        flashSize: plan.flashSize,
        // Flash image partitions without full chip erase to preserve NVS data.
        eraseAll: false,
        compress: true,
        reportProgress: (fileIndex, sent, ofImage) => {
            const file = plan.files[fileIndex];
            if (file === undefined) return;
            if (fileIndex !== current) {
                current = fileIndex;
                written = plan.files.slice(0, fileIndex).reduce((bytes, done) => bytes + done.data.length, 0);
            }
            const share = ofImage > 0 ? (sent / ofImage) * file.data.length : file.data.length;
            position = Math.round(written + share);
            task.progress(position, { detail: name(file.path) });
        },
    });
    task.progress(total, { detail: "resetting the board" });

    await loader.after("hard_reset");
}

/** Connects transport at monitor baud rate and clears RTS/DTR reset signals. */
async function openMonitor(transport: Transport): Promise<void> {
    await transport.connect(MONITOR_BAUD);
    await transport.setDTR(false);
    await transport.setRTS(false);
}

/** The port by its USB-to-UART bridge: "Silicon Labs 10c4:ea60". */
function describePort(info: SerialPortInfo): string {
    if (info.usbVendorId === undefined || info.usbProductId === undefined) return "a serial port";
    const ids = `${hex4(info.usbVendorId)}:${hex4(info.usbProductId)}`;
    const vendor = USB_VENDORS.get(info.usbVendorId);
    return vendor === undefined ? ids : `${vendor} ${ids}`;
}

/** Extracts filename from path string. */
function name(path: string): string {
    return path.slice(path.lastIndexOf("/") + 1);
}

function hex4(value: number): string {
    return value.toString(16).padStart(4, "0");
}

function message(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}

/**
 * esptool-js's lines as they arrive, for a task's detail: what it is doing
 * now, such as "Connecting....", without the line per block it writes.
 */
function loaderTerminal(show: (line: string) => void): IEspLoaderTerminal {
    let line = "";
    return {
        clean: () => {},
        write: (data: string) => {
            line += data;
            show(line.trim());
        },
        writeLine: (data: string) => {
            const whole = (line + data).trim();
            line = "";
            if (whole !== "" && !PER_BLOCK.test(whole)) show(whole);
        },
    };
}
